import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PostCard from '@/components/feature/PostCard';
import { useSeo } from '@/hooks/useSeo';
import { categories } from '@/mocks/categories';
import {
  countByCategory,
  getCategoryBySlug,
  getHotPosts,
  getPostsByCategory,
  toneClasses,
} from '@/utils/content';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug);
  const list = category ? getPostsByCategory(category.slug) : [];
  const hot = getHotPosts(5);
  const tone = toneClasses[category?.tone ?? 'primary'];

  useSeo({
    title: category
      ? `${category.name}分类合集 - 瓜田爆料`
      : '分类合集 - 瓜田爆料',
    description: category
      ? `${category.description}。${category.intro}共收录 ${list.length} 篇话题整理，支持分区浏览。`
      : '瓜田爆料分类合集页面。',
    keywords: category ? category.keywords.join(',') : '瓜田爆料,分类合集',
    canonicalPath: `/category/${category?.slug ?? ''}`,
    ogType: 'website',
    jsonLd: category
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name}分类合集`,
            url: `https://yoyoermei.cn/category/${category.slug}`,
            description: category.intro,
            inLanguage: 'zh-CN',
            isPartOf: { '@type': 'WebSite', name: '瓜田爆料', url: 'https://yoyoermei.cn/' },
            mainEntity: {
              '@type': 'ItemList',
              numberOfItems: list.length,
              itemListElement: list.map((post, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: post.title,
                url: `https://yoyoermei.cn/post/${post.slug}`,
              })),
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://yoyoermei.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: category.name,
                item: `https://yoyoermei.cn/category/${category.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 text-center">
          <i className="ri-error-warning-line text-4xl text-primary-500"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
            没有找到这个分类
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            该分类可能已调整或暂未开放，可以先回到首页浏览全部分区。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '分类合集', to: '/#categories' },
              { label: category.name },
            ]}
          />
          <div className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span className={`w-12 h-12 rounded-md flex items-center justify-center ${tone.soft}`}>
                  <i className={`${category.icon} text-xl`}></i>
                </span>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  {category.name}
                  <span className="ml-2 text-lg md:text-xl font-medium text-foreground-500">
                    合集
                  </span>
                </h1>
              </div>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                {category.intro}
              </p>
            </div>
            <div className="flex items-center gap-6 shrink-0">
              <div>
                <div className="font-heading text-2xl font-bold text-foreground-950">{list.length}</div>
                <div className="mt-1 text-xs text-foreground-500">收录话题</div>
              </div>
              <div className="w-px h-9 bg-background-300"></div>
              <div>
                <div className="font-heading text-2xl font-bold text-foreground-950">
                  {categories.length}
                </div>
                <div className="mt-1 text-xs text-foreground-500">全部专区</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                本分类全部话题
              </h2>
              <span className="text-xs text-foreground-500">按发布时间排序</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {list.map((post) => (
                <PostCard key={post.slug} post={post} variant="default" />
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 lg:sticky lg:top-24">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                切换其它分区
              </h3>
              <ul className="space-y-1">
                {categories.map((item) => {
                  const itemTone = toneClasses[item.tone];
                  const isCurrent = item.slug === category.slug;
                  return (
                    <li key={item.slug}>
                      <Link
                        to={`/category/${item.slug}`}
                        className={`flex items-center justify-between rounded-md px-3 py-2.5 text-sm transition-colors cursor-pointer ${
                          isCurrent
                            ? 'bg-primary-500 text-background-50 font-medium'
                            : 'text-foreground-700 hover:bg-background-100 hover:text-primary-700'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className={`${item.icon} ${isCurrent ? '' : itemTone.text}`}></i>
                          {item.name}
                        </span>
                        <span className={isCurrent ? 'text-background-100' : 'text-foreground-400'}>
                          {countByCategory(item.slug)}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-fire-line text-primary-500"></i>
                <h3 className="font-heading text-lg font-bold text-foreground-950">全站热读</h3>
              </div>
              {hot.map((post, index) => (
                <PostCard key={post.slug} post={post} variant="row" index={index} />
              ))}
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}